import { useMemo } from "react";
import { useAuthStore } from "@/stores/authStore";
import type { PlatformCapability, PlatformUserRole } from "@/types";

/**
 * Platform role hierarchy (lower number = higher privilege)
 */
const ROLE_HIERARCHY: Record<PlatformUserRole, number> = {
  SUPER_ADMIN: 1,
  SUPPORT_ADMIN: 10,
  SUPPORT_AGENT: 20,
  CHANNEL_PARTNER: 30,
};

/**
 * Hook for checking platform user roles in the admin portal
 *
 * @example
 * const { hasRole, isSuperAdmin, canManageTenants } = usePermissions();
 *
 * if (hasRole('SUPPORT_ADMIN')) {
 *   // Show admin features
 * }
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user);

  const userRole = useMemo(() => {
    return user?.role || null;
  }, [user?.role]);

  const userRoleLevel = useMemo(() => {
    return userRole ? (ROLE_HIERARCHY[userRole] ?? 999) : 999;
  }, [userRole]);

  /**
   * Check if user has a specific role or higher in hierarchy
   * @param role - Role to check against
   * @returns true if user's role is equal to or higher than specified role
   */
  const hasRole = (role: PlatformUserRole): boolean => {
    if (!userRole) return false;
    const requiredLevel = ROLE_HIERARCHY[role] ?? 999;
    return userRoleLevel <= requiredLevel;
  };

  /**
   * Check if user has any of the given roles (with hierarchy)
   */
  const hasAnyRole = (roles: PlatformUserRole[]): boolean => {
    return roles.some((role) => hasRole(role));
  };

  /**
   * Check if user has exact role match (no hierarchy)
   */
  const hasExactRole = (role: PlatformUserRole): boolean => {
    return userRole === role;
  };

  const hasCapability = (capability: PlatformCapability): boolean =>
    user?.capabilities?.includes(capability) ?? false;

  // ═══════════════════════════════════════════════════════════════════════════
  // CONVENIENCE GETTERS
  // ═══════════════════════════════════════════════════════════════════════════

  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isSupportAdmin = hasRole("SUPPORT_ADMIN");
  const isSupportAgent = hasRole("SUPPORT_AGENT");
  const isChannelPartner = hasExactRole("CHANNEL_PARTNER");

  // ═══════════════════════════════════════════════════════════════════════════
  // FEATURE ACCESS
  // ═══════════════════════════════════════════════════════════════════════════

  /** Can manage roles, permissions, and system settings */
  const canManageGovernance = hasCapability("GOVERNANCE_MANAGE");

  /** Can impersonate tenant users */
  const canImpersonate = hasCapability("IMPERSONATE");

  /** Can onboard new tenants */
  const canOnboardTenants = hasCapability("TENANT_ONBOARD");

  /** Can manage subscriptions and plans */
  const canManageSubscriptions = hasCapability("SUBSCRIPTION_MANAGE");

  /** Can view subscriptions */
  const canViewSubscriptions = hasCapability("SUBSCRIPTION_VIEW");

  /** Can view tenant details */
  const canViewTenants = hasCapability("TENANT_VIEW");

  /** Can manage tenants (create, update, delete) */
  const canManageTenants = hasCapability("TENANT_MANAGE");

  /** Can view analytics */
  const canViewAnalytics = hasCapability("MONITORING_VIEW");

  /** Can handle support tickets */
  const canHandleSupport = hasCapability("SUPPORT_MANAGE");

  /** Can manage system modules */
  const canManageModules = hasCapability("MODULE_MANAGE");

  /** Can manage system settings */
  const canManageSettings = hasCapability("SETTINGS_MANAGE");

  return {
    // User info
    user,
    userRole,

    // Role checks
    hasRole,
    hasAnyRole,
    hasExactRole,
    hasCapability,

    // Convenience getters
    isSuperAdmin,
    isSupportAdmin,
    isSupportAgent,
    isChannelPartner,

    // Feature access
    canManageGovernance,
    canImpersonate,
    canOnboardTenants,
    canManageSubscriptions,
    canViewSubscriptions,
    canViewTenants,
    canManageTenants,
    canViewAnalytics,
    canHandleSupport,
    canManageModules,
    canManageSettings,
  };
}
